import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarClock,
  CheckCircle2,
  Download,
  Layers,
  CircleSlash,
  Search,
  X,
} from "lucide-react";
import { Badge } from "../../../components/ui/Badge";
import { EmptyState } from "../../../components/ui/EmptyState";
import { KpiFilterCards } from "../../../components/ui/KpiFilterCards";
import { Table, TBody, TD, TH, THead, TR } from "../../../components/ui/Table";
import { downloadCsv } from "../../../utils/exportCsv";
import { formatCLP } from "../../../utils/formatters";
import { normName } from "../../../api/recolecciones";
import { DayBar } from "./DayBar";
import { usePdfViewer } from "./usePdfViewer";
import { hhmm, horaDe, iniciales, primerPickup } from "./recoleccionesShared";

// Los tres estados reales de una parada. `en_curso` y `fallida` estuvieron acá como
// placeholders "(En construcción)" que el back nunca emitió; "fallida" era además el
// nombre provisorio de lo que hoy es "anulada", así que se reemplaza por ella.
const ESTADO_CARDS = [
  { value: "", label: "Todas", icon: Layers, tone: "brand" },
  { value: "programada", label: "Pendientes", icon: CalendarClock, tone: "neutral" },
  { value: "ejecutada", label: "Realizadas", icon: CheckCircle2, tone: "success" },
  { value: "anulada", label: "Anuladas", icon: CircleSlash, tone: "neutral" },
];

const numOrDash = (v) => (v !== null && v !== undefined && v !== "" ? v : "—");

// Neutraliza formula injection en Excel/Sheets: comercio/dirección son texto
// controlado por terceros y una celda que empieza con =,+,-,@ se ejecutaría.
const csvSafe = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /^[=+\-@]/.test(s) ? `'${s}` : s;
};

/**
 * RetirosTab — detalle plano parada a parada del día (port del tab "Retiros"
 * del mockup mu-portal): filtros por estado, búsqueda libre y descarga CSV del
 * día con los filtros activos aplicados.
 */
export function RetirosTab({ loading, instancias, selectedDate, onSelectedDateChange, todayYmd }) {
  const { t } = useTranslation();
  const { pdfLink, pdfModal } = usePdfViewer();
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState("");

  // Paradas aplanadas del día con su ruta/chofer, ordenadas por ventana.
  const rows = useMemo(() => {
    const out = [];
    for (const inst of instancias) {
      for (const p of inst.paradas) {
        out.push({ ...p, ruta: inst.nombre, rutaId: inst.id, chofer: inst.chofer || "" });
      }
    }
    return out.sort((a, b) => {
      const va = hhmm(a.ventanaDesde) || "99:99";
      const vb = hhmm(b.ventanaDesde) || "99:99";
      return va.localeCompare(vb) || a.comercio.localeCompare(b.comercio);
    });
  }, [instancias]);

  const counts = useMemo(() => {
    const c = { programada: 0, ejecutada: 0, anulada: 0 };
    for (const r of rows) c[r.estado] = (c[r.estado] ?? 0) + 1;
    return c;
  }, [rows]);

  const filas = useMemo(() => {
    const s = normName(search);
    return rows.filter((r) => {
      if (estado && r.estado !== estado) return false;
      if (!s) return true;
      return (
        normName(r.comercio).includes(s) ||
        normName(r.direccion).includes(s) ||
        normName(r.comuna).includes(s) ||
        normName(r.ruta).includes(s) ||
        normName(r.chofer).includes(s) ||
        (r.pickups || []).some((pk) => String(pk.correlative || "").includes(s))
      );
    });
  }, [rows, estado, search]);

  const clearAll = () => {
    setSearch("");
    setEstado("");
  };

  // CSV del día con los filtros activos aplicados (`;` + BOM: abre en Excel CL).
  const handleExport = () => {
    const data = filas.map((r) => ({
      Fecha: selectedDate,
      Ruta: csvSafe(r.ruta),
      Comercio: csvSafe(r.comercio),
      Direccion: csvSafe(r.direccion),
      Comuna: csvSafe(r.comuna),
      "Ventana desde": hhmm(r.ventanaDesde) || "",
      "Ventana hasta": hhmm(r.ventanaHasta) || "",
      "Pedidos esperados": r.pedidosEsperados ?? "",
      "Bultos esperados": r.bultosEsperados ?? "",
      "Peso esperado (kg)": r.pesoEsperadoKg ?? "",
      "Cobrar (CLP)": r.valorCobrarCliente ?? "",
      "Pagar repartidor (CLP)": r.valorPagarRepartidor ?? "",
      Conductor: csvSafe(r.chofer),
      Estado:
        r.estado === "ejecutada"
          ? t("Realizada")
          : r.estado === "anulada"
            ? t("Anulada")
            : t("Pendiente"),
      Retiros: (r.pickups || []).length,
      "Primer retiro": (() => {
        const primero = primerPickup(r.pickups);
        return primero?.date ? horaDe(primero.date) : "";
      })(),
      Correlativos: (r.pickups || []).map((pk) => `#${pk.correlative}`).join(" "),
    }));
    downloadCsv(`retiros-${selectedDate.replace(/-/g, "")}`, data);
  };

  return (
    <div className="space-y-4">
      {pdfModal}
      <DayBar
        selectedDate={selectedDate}
        onSelectedDateChange={onSelectedDateChange}
        todayYmd={todayYmd}
        extra={
          <button
            className="rec-btn rec-btn--secondary rec-btn--sm"
            onClick={handleExport}
            disabled={filas.length === 0}
            title={t("Descargar CSV del día con los filtros activos")}
          >
            <Download size={14} />
            {t("Descargar CSV")}
          </button>
        }
      />

      <KpiFilterCards
        cards={ESTADO_CARDS.map((c) => ({
          ...c,
          label: c.pending ? (
            <>
              {t(c.label)}{" "}
              <span className="text-[10px] font-normal text-fg-subtle">({t("En construcción")})</span>
            </>
          ) : (
            t(c.label)
          ),
        }))}
        counts={counts}
        total={rows.length}
        active={estado}
        onChange={setEstado}
      />

      <div className="rec-searchrow">
        <div className="rec-search">
          <Search size={16} className="rec-search__icon" />
          <input
            type="search"
            placeholder={t("Buscar por ruta, comercio, dirección o correlativo…")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {(search || estado) && (
          <button className="rec-btn rec-btn--ghost" onClick={clearAll}>
            <X size={16} />
            {t("Limpiar")}
          </button>
        )}
      </div>

      {loading ? (
        <div className="rec-card rec-skel">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rec-skel__row" />
          ))}
        </div>
      ) : filas.length === 0 ? (
        <div className="rec-card">
          <EmptyState
            icon={<Search />}
            title={t("Sin retiros para este día")}
            description={
              rows.length === 0
                ? t("Ninguna ruta de retiro está programada para esta fecha (según su frecuencia).")
                : t("No hay paradas que calcen con los filtros activos.")
            }
          />
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-border bg-bg shadow-card overflow-hidden">
            <Table>
              <THead>
                <TR>
                  <TH>{t("Comercio")}</TH>
                  <TH>{t("Dirección")}</TH>
                  <TH>{t("Ruta")}</TH>
                  <TH>{t("Ventana")}</TH>
                  <TH>{t("Pedidos est.")}</TH>
                  <TH>{t("Bultos")}</TH>
                  <TH>{t("Peso est.")}</TH>
                  <TH>{t("Cobrar")}</TH>
                  <TH>{t("Pagar")}</TH>
                  <TH>{t("Chofer")}</TH>
                  <TH>{t("Estado")}</TH>
                  <TH>{t("Retiro")}</TH>
                </TR>
              </THead>
              <TBody>
                {filas.map((r, i) => (
                  <TR key={`${r.rutaId}|${r.id}|${i}`}>
                    <TD>
                      <div className="text-sm font-medium text-fg">{r.comercio}</div>
                    </TD>
                    <TD className="max-w-[220px]">
                      <div className="text-sm text-fg truncate">{r.direccion || "—"}</div>
                      {r.comuna && <div className="text-xs text-fg-muted">{r.comuna}</div>}
                    </TD>
                    <TD className="max-w-[180px]">
                      <div className="text-sm text-fg truncate">{r.ruta}</div>
                    </TD>
                    <TD className="whitespace-nowrap">
                      <span className="text-sm text-fg-muted rec-mono">
                        {hhmm(r.ventanaDesde) && hhmm(r.ventanaHasta)
                          ? `${hhmm(r.ventanaDesde)}–${hhmm(r.ventanaHasta)}`
                          : "—"}
                      </span>
                    </TD>
                    <TD>
                      <span className="text-sm tabular-nums text-fg-muted">{numOrDash(r.pedidosEsperados)}</span>
                    </TD>
                    <TD>
                      <span className="text-sm tabular-nums text-fg-muted">{numOrDash(r.bultosEsperados)}</span>
                    </TD>
                    <TD>
                      <span className="text-sm tabular-nums text-fg-muted">
                        {r.pesoEsperadoKg !== null && r.pesoEsperadoKg !== undefined && r.pesoEsperadoKg !== ""
                          ? `${r.pesoEsperadoKg} kg`
                          : "—"}
                      </span>
                    </TD>
                    <TD className="tabular-nums text-sm text-fg-muted">
                      {Number(r.valorCobrarCliente) > 0 ? formatCLP(Number(r.valorCobrarCliente)) : "—"}
                    </TD>
                    <TD className="tabular-nums text-sm text-fg-muted">
                      {Number(r.valorPagarRepartidor) > 0 ? formatCLP(Number(r.valorPagarRepartidor)) : "—"}
                    </TD>
                    <TD>
                      {r.chofer ? (
                        <span className="inline-flex items-center gap-2 min-w-0">
                          <span className="rec-avatar rec-avatar--xs">{iniciales(r.chofer)}</span>
                          <span className="text-xs text-fg truncate max-w-[110px]">{r.chofer}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-fg-subtle italic">{t("Sin asignar")}</span>
                      )}
                    </TD>
                    <TD>
                      {r.estado === "ejecutada" ? (
                        <Badge tone="success" dot>{t("Realizada")}</Badge>
                      ) : r.estado === "anulada" ? (
                        <Badge tone="muted" dot>{t("Anulada")}</Badge>
                      ) : (
                        <Badge tone="neutral" dot>{t("Pendiente")}</Badge>
                      )}
                    </TD>
                    <TD className="whitespace-nowrap">
                      {(r.pickups || []).length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          {r.pickups.slice(0, 3).map((pk, j) => (
                            <span key={pk._id || pk.correlative || j} className="inline-flex items-center gap-1.5 text-xs tabular-nums text-fg">
                              <span className="rec-mono">#{pk.correlative}</span>
                              <span className="text-fg-muted">{horaDe(pk.date)}</span>
                              {pk.pdfUrl && (
                                pdfLink({
                                  url: pk.pdfUrl,
                                  correlative: pk.correlative,
                                  className: "rec-link",
                                  title: t("Ver PDF"),
                                  children: "PDF",
                                })
                              )}
                            </span>
                          ))}
                          {r.pickups.length > 3 && (
                            <span className="text-[11px] text-fg-subtle">
                              +{r.pickups.length - 3} {t("retiros")}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-fg-subtle">—</span>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
          <p className="text-xs text-fg-muted">
            {t("Mostrando {{v}} de {{n}} retiros del día.", { v: filas.length, n: rows.length })}
          </p>
        </>
      )}
    </div>
  );
}
